import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatCurrency } from '../../utils/formatters'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import Table, { Column } from '../../components/ui/Table'
import { useBills, useDeleteBill, usePrintBill, useViewBillInvoice } from '../../hooks/api/useBills'
import CreateBillModal from '../../components/bills/CreateBillModal'
import { useBookers } from '../../hooks/api/useBookers'
import { useAreas } from '../../hooks/api/useAreas'
import { Bill } from '../../types'
import { format } from 'date-fns'
import StatusChip from '../../components/ui/StatusChip'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

const formatBillQuantity = (bill: Bill, uomCode: string) => {
  const items = bill.sku_items || []
  if (items.length === 0) {
    return `${Number(bill.summary.quantity_ctn).toLocaleString('en-US', { maximumFractionDigits: 3 })} ${uomCode}`
  }

  const primaryUnits = items.reduce(
    (total, item) => total + (item.full_quantity_ctn ?? item.quantity_ctn), 0
  )
  const loosePieces = items.reduce((total, item) => total + (item.loose_pcs ?? 0), 0)
  const parts = []
  if (primaryUnits > 0) {
    parts.push(`${primaryUnits.toLocaleString('en-US', { maximumFractionDigits: 3 })} ${uomCode}`)
  }
  if (loosePieces > 0) {
    parts.push(`${loosePieces.toLocaleString('en-US')} PCS`)
  }
  return parts.join(' + ') || `0 ${uomCode}`
}

const BillListPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [customerName, setCustomerName] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [bookerFilter, setBookerFilter] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [createBillOpen, setCreateBillOpen] = useState(false)

  const { data: billsData, isLoading } = useBills({
    page: page + 1,
    limit: rowsPerPage,
    customer_name: customerName,
    status: statusFilter,
    payment_status: paymentStatusFilter,
    ...(bookerFilter && { booker_id: bookerFilter }),
    area: areaFilter,
    date: dateFilter,
  })

  const deleteBillMutation = useDeleteBill()
  const printBillMutation = usePrintBill()
  const viewBillInvoiceMutation = useViewBillInvoice()
  const { data: bookersData } = useBookers({ page: 1, limit: 100 })
  const { data: areasData } = useAreas()
  const bookers = bookersData?.data || []
  const areas = areasData?.results || []

  const handleCustomerNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerName(event.target.value)
    setPage(0)
  }

  const handleStatusFilter = (event: any) => {
    setStatusFilter(event.target.value)
    setPage(0)
  }

  const handleDateFilter = (event: any) => {
    setDateFilter(event.target.value)
    setPage(0)
  }

  const handleBookerFilter = (event: any) => {
    setBookerFilter(event.target.value)
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bill: Bill) => {
    setAnchorEl(event.currentTarget)
    setSelectedBill(bill)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedBill(null)
  }

  const handleDeleteClick = () => {
    setAnchorEl(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setSelectedBill(null)
  }

  const handleDeleteConfirm = () => {
    if (selectedBill) {
      deleteBillMutation.mutate(selectedBill._id, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setSelectedBill(null)
        }
      })
    }
  }

  const handleViewInvoice = () => {
    if (selectedBill) {
      viewBillInvoiceMutation.mutate(selectedBill._id)
      handleMenuClose()
    }
  }

  const handlePrint = () => {
    if (selectedBill) {
      printBillMutation.mutate(selectedBill._id)
      handleMenuClose()
    }
  }

  const getBillStatusVariant = (status: string): 'success' | 'info' | 'danger' | 'neutral' => {
    switch (status) {
      case 'active':    return 'success'
      case 'serviced':  return 'info'
      case 'cancelled': return 'danger'
      default:          return 'neutral'
    }
  }

  const getPaymentVariant = (status?: string): 'success' | 'warn' | 'danger' | 'neutral' => {
    switch (status) {
      case 'paid':    return 'success'
      case 'partial': return 'warn'
      case 'unpaid':  return 'danger'
      default:        return 'neutral'
    }
  }

  const getPaymentLabel = (status?: string) => {
    switch (status) {
      case 'paid':    return 'Settled'
      case 'partial': return 'Partial'
      case 'unpaid':  return 'Unpaid'
      default:        return '—'
    }
  }

  const columns: Column<Bill>[] = [
    {
      id: 'customer_name',
      label: 'Customer Name',
      minWidth: 150,
      render: (value, row) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {value}
          {row.is_sales_tax_invoice && (
            <Chip
              icon={<ReceiptIcon />}
              label="STI"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      ),
    },
    {
      id: 'booker_id',
      label: 'Booker',
      minWidth: 120,
      render: (value) => value?.name || 'N/A',
    },
    {
      id: 'area_id',
      label: 'Area',
      minWidth: 120,
      render: (value) => value?.area_name || 'N/A',
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 80,
      render: (value) => <StatusChip label={value} variant={getBillStatusVariant(value)} />,
    },
    {
      id: 'payment_status',
      label: 'Payment',
      minWidth: 90,
      render: (value) => <StatusChip label={getPaymentLabel(value)} variant={getPaymentVariant(value)} />,
    },
    {
      id: 'summary',
      label: 'Quantity',
      minWidth: 150,
      align: 'right',
      render: (_, row) => formatBillQuantity(row, uom.code),
    },
    {
      id: 'summary',
      label: 'Net Payable',
      minWidth: 120,
      align: 'right',
      render: (_, row) => `Rs ${formatCurrency(row.summary.net_payable)}`,
    },
    {
      id: 'discount',
      label: 'Discount %',
      minWidth: 100,
      align: 'right',
      render: (value) => `${value}%`,
    },
    ...(advanceTaxEnabled ? ([{
      id: 'adv_tax',
      label: 'Adv Tax %',
      minWidth: 100,
      align: 'right',
      render: (value) => `${value}%`,
    }] as Column<Bill>[]) : []),
    {
      id: 'updatedAt',
      label: 'Updated',
      minWidth: 180,
      render: (value) => format(new Date(value), 'MMM dd, yyyy hh:mm a'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 50,
      align: 'center',
      render: (_, row) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bills</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateBillOpen(true)}>
          Create Bill
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              placeholder="Customer Name"
              value={customerName}
              onChange={handleCustomerNameChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilter}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="serviced">Serviced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Payment</InputLabel>
              <Select
                value={paymentStatusFilter}
                onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(0) }}
                label="Payment"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="paid">Settled</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Booker</InputLabel>
              <Select
                value={bookerFilter}
                onChange={handleBookerFilter}
                label="Booker"
              >
                <MenuItem value="">All</MenuItem>
                {bookers.map((booker: any) => (
                  <MenuItem key={booker._id} value={booker._id}>
                    {booker.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Area</InputLabel>
              <Select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                label="Area"
              >
                <MenuItem value="">All Areas</MenuItem>
                {areas.map((area) => (
                  <MenuItem key={area._id} value={area.area_name}>
                    {area.area_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              value={dateFilter}
              onChange={handleDateFilter}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Table
        columns={columns}
        onRowClick={(bill) => navigate(`/bills/${bill._id}`)}
        data={billsData?.data || []}
        loading={isLoading}
        pagination={billsData ? {
          page,
          rowsPerPage,
          total: billsData.total,
          onPageChange: setPage,
          onRowsPerPageChange: setRowsPerPage,
        } : undefined}
      />

      <CreateBillModal open={createBillOpen} onClose={() => setCreateBillOpen(false)} />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewInvoice} disabled={viewBillInvoiceMutation.isPending}>
          <VisibilityIcon sx={{ mr: 1 }} />
          {viewBillInvoiceMutation.isPending ? 'Loading...' : 'View Invoice'}
        </MenuItem>
        <MenuItem onClick={() => navigate(`/bills/${selectedBill?._id}/edit?from=table`)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handlePrint} disabled={printBillMutation.isPending}>
          <PrintIcon sx={{ mr: 1 }} />
          {printBillMutation.isPending ? 'Printing...' : 'Print'}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bill? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteBillMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BillListPage
